import React, { useState } from 'react';

function renderQuestionText(text) {
  if (!text) { return null; }
  const lines = text.split('\n');
  const elements = [];
  let bulletBuffer = [];

  const flushBullets = () => {
    if (bulletBuffer.length === 0) { return; }
    elements.push(
      <ul key={`ul-${elements.length}`} className="list-disc pl-5 my-2 space-y-1 text-left">
        {bulletBuffer.map((b, i) => <li key={i}>{b}</li>)}
      </ul>
    );
    bulletBuffer = [];
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const bulletMatch = line.match(/^\s*\*\s*(.*)/);
    if (bulletMatch) {
      bulletBuffer.push(bulletMatch[1]);
    } else {
      flushBullets();
      if (line.trim() !== '') {
        elements.push(<p key={`p-${i}`} className="mb-2">{line}</p>);
      }
    }
  }
  flushBullets();
  return elements;
}

export default function ResponseOptionSelector({ options, onSelect, questionText, responseType }) {
  const [submitting, setSubmitting] = useState(null);
  const [scaleValue, setScaleValue] = useState(null);

  const sortedOptions = [...(options || [])].sort((a, b) => a.displayOrder - b.displayOrder);

  async function handleClick(responseValue) {
    setSubmitting(responseValue);
    await onSelect(responseValue);
  }

  if (responseType === 'scale' && sortedOptions.length > 0 && sortedOptions[0].rangeMin !== null && sortedOptions[0].rangeMin !== undefined) {
    const scaleMin = Math.min(...sortedOptions.map((o) => o.rangeMin));
    const scaleMax = Math.max(...sortedOptions.map((o) => o.rangeMax));
    const count = scaleMax - scaleMin + 1;
    const useButtons = count <= 5;

    return (
      <div className="bg-white rounded-lg shadow-sm p-8">
        <div className="text-gray-800 text-base leading-relaxed mb-6">
          {questionText ? renderQuestionText(questionText) : (
            <h1 className="text-xl font-semibold text-gray-900 text-center">What do you think?</h1>
          )}
        </div>
        {useButtons ? (
          <div className="flex gap-2 justify-center mb-4">
            {Array.from({ length: count }, (_, i) => scaleMin + i).map((val) => (
              <button
                key={val}
                onClick={() => handleClick(String(val))}
                disabled={submitting !== null}
                className="min-w-[44px] min-h-[44px] px-4 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting === String(val) ? '...' : val}
              </button>
            ))}
          </div>
        ) : (
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-500 mb-2">
              <span>{scaleMin}</span>
              <span>{scaleMax}</span>
            </div>
            <input
              type="range"
              min={scaleMin}
              max={scaleMax}
              value={scaleValue ?? Math.floor((scaleMin + scaleMax) / 2)}
              onChange={(e) => setScaleValue(parseInt(e.target.value, 10))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
              aria-label="Scale selection"
            />
            <div className="text-center mt-2 text-2xl font-bold text-emerald-700">
              {scaleValue ?? Math.floor((scaleMin + scaleMax) / 2)}
            </div>
            <button
              onClick={() => handleClick(String(scaleValue ?? Math.floor((scaleMin + scaleMax) / 2)))}
              disabled={submitting !== null}
              className="w-full mt-4 min-h-[44px] px-6 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-8">
      <div className="text-gray-800 text-base leading-relaxed mb-6">
        {questionText ? renderQuestionText(questionText) : (
          <h1 className="text-xl font-semibold text-gray-900 text-center">What do you think?</h1>
        )}
      </div>
      <div className="space-y-3">
        {sortedOptions.map((option) => (
          <button
            key={option.responseValue}
            onClick={() => handleClick(option.responseValue)}
            disabled={submitting !== null}
            className="w-full min-h-[44px] px-6 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting === option.responseValue ? 'Submitting...' : option.buttonLabel}
          </button>
        ))}
      </div>
    </div>
  );
}
